import { useState, useEffect } from 'react';
import SharedDialog from '../../../../../global/Modal/Modal';
import { getSourceList } from '../../helpers/sourceListHelpers';
import { searchContext } from '../../../../../Context/Context.jsx';
import { SearchContext } from '../../../../../types';
import { XMarkIcon } from '@heroicons/react/16/solid';

interface ScoreModalProps {
  isOpen: boolean;
  onClose: () => void;
}

interface SelectedSource {
  name: string;
  score: number;
}

export default function ScoreModal({ isOpen, onClose }: ScoreModalProps) {
  const { tab, restrictSources } = searchContext() as unknown as SearchContext;
  const [availableSources, setAvailableSources] = useState<string[]>([]);
  const [selectedSources, setSelectedSources] = useState<SelectedSource[]>([]);
  const [showWarning, setShowWarning] = useState(false);

  useEffect(() => {
    const sourceList = getSourceList({ tab, restrictSources });
    let sources: string[] = [];

    if (Array.isArray(sourceList)) {
      sources = sourceList;
    } else {
      sources = Object.values(sourceList).flat();
    }

    setAvailableSources(sources);
  }, [tab, restrictSources]);

  const getTotalScore = () => {
    return selectedSources.reduce((sum, source) => sum + source.score, 0);
  };

  const handleAddSource = (sourceName: string) => {
    if (selectedSources.find(s => s.name === sourceName)) {
      return;
    }

    const currentTotal = getTotalScore();
    const remainingScore = 1 - currentTotal;

    if (remainingScore <= 0) {
      setShowWarning(true);
      return;
    }

    // Add source with remaining score or a default small value
    const defaultScore = Math.min(0.1, remainingScore);
    setSelectedSources([...selectedSources, { name: sourceName, score: defaultScore }]);
    setShowWarning(false);
  };

  const handleRemoveSource = (sourceName: string) => {
    setSelectedSources(selectedSources.filter(s => s.name !== sourceName));
    setShowWarning(false);
  };

  const handleScoreChange = (sourceName: string, newScore: number) => {
    const otherSourcesTotal = selectedSources
      .filter(s => s.name !== sourceName)
      .reduce((sum, s) => sum + s.score, 0);

    const maxAllowedScore = 1 - otherSourcesTotal;

    if (newScore > maxAllowedScore) {
      setShowWarning(true);
      return;
    }

    setSelectedSources(selectedSources.map(s => (s.name === sourceName ? { ...s, score: newScore } : s)));
    setShowWarning(false);
  };

  const handleApply = () => {
    console.log('Applied scores:', selectedSources);
    onClose();
  };

  const totalScore = getTotalScore();
  const isMaxReached = totalScore >= 1;

  return (
    <SharedDialog
      isOpen={isOpen}
      onClose={onClose}
      title="Score Configuration"
      confirmText="Apply"
      hasConfirmBtn
      cancelText="Cancel"
      onConfirm={handleApply}
      className="score-modal"
    >
      <div className="space-y-4">
        {/* Source Selector */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">Select Sources</label>
          <select
            className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
            onChange={e => {
              if (e.target.value) {
                handleAddSource(e.target.value);
                e.target.value = '';
              }
            }}
            disabled={isMaxReached}
          >
            <option value="">Choose a source...</option>
            {availableSources
              .filter(source => !selectedSources.find(s => s.name === source))
              .map(source => (
                <option key={source} value={source}>
                  {source.replace('_', ' ')}
                </option>
              ))}
          </select>
        </div>

        {/* Warning Message */}
        {showWarning && (
          <div className="bg-yellow-50 border border-yellow-200 rounded-md p-3">
            <p className="text-sm text-yellow-800">
              ⚠️ The total score cannot exceed 1.0. Please adjust the existing scores.
            </p>
          </div>
        )}

        {/* Total Score Indicator */}
        <div className="bg-gray-50 rounded-md p-3">
          <div className="flex justify-between items-center">
            <span className="text-sm font-medium text-gray-700">Total Score:</span>
            <span
              className={`text-lg font-bold ${
                totalScore > 1 ? 'text-red-600' : totalScore === 1 ? 'text-green-600' : 'text-gray-900'
              }`}
            >
              {totalScore.toFixed(2)} / 1.00
            </span>
          </div>
          <div className="mt-2 w-full bg-gray-200 rounded-full h-2">
            <div
              className={`h-2 rounded-full transition-all ${
                totalScore > 1 ? 'bg-red-500' : totalScore === 1 ? 'bg-green-500' : 'bg-blue-500'
              }`}
              style={{ width: `${Math.min(totalScore * 100, 100)}%` }}
            />
          </div>
        </div>

        {/* Selected Sources with Sliders */}
        <div className="space-y-3">
          {selectedSources.length === 0 ? (
            <p className="text-sm text-gray-500 text-center py-4">
              No sources selected yet. Choose sources from the dropdown above.
            </p>
          ) : (
            selectedSources.map(source => (
              <div key={source.name} className="bg-white border border-gray-200 rounded-md p-3 shadow-sm">
                <div className="flex items-center justify-between mb-2">
                  <span className="font-medium text-gray-900">{source.name.replace('_', ' ')}</span>
                  <button
                    onClick={() => handleRemoveSource(source.name)}
                    className="text-gray-400 hover:text-red-600 transition-colors"
                  >
                    <XMarkIcon className="w-5 h-5" />
                  </button>
                </div>
                <div className="flex items-center gap-3">
                  <input
                    type="range"
                    min={0}
                    max={1}
                    step={0.1}
                    value={source.score}
                    onChange={e => handleScoreChange(source.name, parseFloat(e.target.value))}
                    className="flex-1 h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer"
                  />
                  <input
                    type="number"
                    min={0}
                    max={1}
                    step={0.1}
                    value={source.score}
                    onChange={e => {
                      const value = parseFloat(e.target.value);
                      if (!isNaN(value)) {
                        handleScoreChange(source.name, value);
                      }
                    }}
                    className="w-20 px-2 py-1 text-sm border border-gray-300 rounded text-center"
                  />
                </div>
              </div>
            ))
          )}
        </div>
      </div>
    </SharedDialog>
  );
}
